import { Key, ReactNode, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  CatalogFilterLayout,
  EntityListProvider,
  EntityOwnerPicker,
  EntityTagPicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import {
  EntityListDocsTable,
  TechDocsPicker,
} from '@backstage/plugin-techdocs';
import { RiGridFill, RiTableFill } from '@remixicon/react';
import {
  Container,
  Box,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Flex,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import { CardVariant, Page } from '@internal/backstage-plugin-hm-react';
import { CustomEntityListDocsGrid } from './custom-list-docs-grid';
import { DocsGrid, Group } from './docs-grid';
import { useCuratedTechDocs } from './use-curated-techdocs';
import { useStarredTechDocs } from './use-starred-techdocs';

export { RiFileTextFill as TechDocsLandingPageIcon } from '@remixicon/react';

export type CustomTechDocsHomeProps = {
  groups?: Array<{
    title: ReactNode;
    filterPredicate: ((entity: Entity) => boolean) | string;
  }>;
};

export const featuredTechDocs = [
  { name: 'backstage', onHomepage: true },
  { name: 'mcs-accelerator', onHomepage: true },
  { name: 'threads', onHomepage: true },
  { name: 'erb', onHomepage: true },
  { name: 'frontend-platform-docs', onHomepage: true },
];

export const mostViewedTechDocs = [
  { name: 'clouddc-documentation', onHomepage: true },
  { name: 'architectural-decision-record-submission', onHomepage: true },
  { name: 'hapi', onHomepage: true },
  { name: 'event-platforms', onHomepage: true },
];

const featuredTechDocNames = new Set(
  featuredTechDocs
    .filter(featuredTechDoc => featuredTechDoc.onHomepage)
    .map(featuredTechDoc => featuredTechDoc.name),
);

const mostViewedTechDocNames = new Set(
  mostViewedTechDocs
    .filter(mostViewedTechDoc => mostViewedTechDoc.onHomepage)
    .map(mostViewedTechDoc => mostViewedTechDoc.name),
);

const curatedTechDocNames = [
  ...featuredTechDocNames,
  ...mostViewedTechDocNames,
];

const featured: Group[] = [
  {
    title: null,
    filterPredicate: (entity: Entity) =>
      featuredTechDocNames.has(entity.metadata?.name ?? ''),
    cardVariant: CardVariant.minimal,
  },
];

const mostViewed: Group[] = [
  {
    title: null,
    filterPredicate: (entity: Entity) =>
      mostViewedTechDocNames.has(entity.metadata?.name ?? ''),
    cardVariant: CardVariant.minimal,
  },
];

const all: Group[] = [
  {
    title: 'All TechDocs',
    filterPredicate: () => true,
  },
];

// Normal variant keeps the favourite toggle visible, so docs can be unstarred in place
const starred: Group[] = [
  {
    title: 'Starred TechDocs',
    filterPredicate: () => true,
    withoutTags: true,
    withoutLinks: true,
  },
];

enum ViewMode {
  Table = 'table',
  Cards = 'cards',
}

export const TechDocsLandingPage = () => {
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Table);
  const starredEntities = useStarredTechDocs();
  const curatedEntities = useCuratedTechDocs(curatedTechDocNames);
  const handleToggleView = (keys: Set<Key>) => {
    setViewMode(keys.has(ViewMode.Cards) ? ViewMode.Cards : ViewMode.Table);
  };

  return (
    <Page title="TechDocs">
      <Container>
        <EntityListProvider>
          <Box
            style={{
              margin: '0 calc(-1 * var(--bui-space-2)) var(--bui-space-5)',
            }}
          >
            <Tabs>
              <TabList>
                <Tab id="featured">Featured</Tab>
                <Tab id="most-viewed">Most viewed</Tab>
              </TabList>
              <TabPanel id="featured">
                <DocsGrid entities={curatedEntities} groups={featured} />
              </TabPanel>
              <TabPanel id="most-viewed">
                <DocsGrid entities={curatedEntities} groups={mostViewed} />
              </TabPanel>
            </Tabs>
          </Box>
          <Box mb="6">
            <DocsGrid entities={starredEntities} groups={starred} />
          </Box>

          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <TechDocsPicker />
              <UserListPicker initialFilter="all" />
              <EntityOwnerPicker />
              <EntityTagPicker />
            </CatalogFilterLayout.Filters>

            <CatalogFilterLayout.Content>
              <Flex justify="end" mb="2">
                <ToggleButtonGroup
                  selectionMode="single"
                  disallowEmptySelection
                  selectedKeys={[viewMode]}
                  onSelectionChange={handleToggleView}
                >
                  <ToggleButton
                    id={ViewMode.Table}
                    aria-label="Table view"
                    iconStart={<RiTableFill />}
                  />
                  <ToggleButton
                    id={ViewMode.Cards}
                    aria-label="Card view"
                    iconStart={<RiGridFill />}
                  />
                </ToggleButtonGroup>
              </Flex>
              {viewMode === ViewMode.Table ? (
                <EntityListDocsTable />
              ) : (
                <CustomEntityListDocsGrid groups={all} />
              )}
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Container>
    </Page>
  );
};
